import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { STATUS_LABELS } from '../utils/registrationFilterParams';

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

const optionLabels = (field) => {
  if (field.optionsWithPricing?.length) return field.optionsWithPricing.map((o) => o?.label);
  if (field.optionWithBlackLists?.length) return field.optionWithBlackLists.map((o) => o?.label);
  return field.options || [];
};

// Every form field (sub-fields included) with its selectable answers, de-duplicated by label.
const flattenFields = (fields = [], seen = new Map()) => {
  fields.forEach((field) => {
    if (!field?.label || field.type === 'file') return;
    const existing = seen.get(field.label);
    const options = optionLabels(field).filter(Boolean);
    seen.set(field.label, { label: field.label, options: existing ? [...new Set([...existing.options, ...options])] : options });
    (field.optionsWithPricing || []).forEach((o) => flattenFields(o?.subFields || [], seen));
  });
  return seen;
};

/** Filters stored in the URL, the same params the analytics drill-down writes. */
const RegistrationFilterBar = ({ value, onChange, formSchema = [], purposes = [] }) => {
  const [draftValue, setDraftValue] = useState(value.value);
  useEffect(() => setDraftValue(value.value), [value.value]);

  const fields = Array.from(flattenFields(formSchema).values());
  const selectedField = fields.find((f) => f.label === value.field);
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 rounded-lg border bg-gray-50 p-3">
      <label className="space-y-1 text-xs text-gray-600">
        <span>Status</span>
        <select className={selectClass} value={value.status} onChange={(e) => set({ status: e.target.value })}>
          <option value="">All</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </label>
      <label className="space-y-1 text-xs text-gray-600">
        <span>Source</span>
        <select className={selectClass} value={value.source} onChange={(e) => set({ source: e.target.value })}>
          <option value="">All</option>
          <option value="ONLINE">Online</option>
          <option value="ON-SPOT">On-spot</option>
        </select>
      </label>
      <label className="space-y-1 text-xs text-gray-600">
        <span>Check-in</span>
        <select
          className={selectClass}
          value={value.checkin ? `in:${value.checkin}` : value.notcheckin ? `out:${value.notcheckin}` : ''}
          onChange={(e) => {
            const [state, purpose] = e.target.value.split(/:(.*)/s);
            set({ checkin: state === 'in' ? purpose : '', notcheckin: state === 'out' ? purpose : '' });
          }}
        >
          <option value="">Any</option>
          {purposes.map((p) => (
            <React.Fragment key={p.purpose}>
              <option value={`in:${p.purpose}`}>{p.name || p.purpose}: checked in</option>
              <option value={`out:${p.purpose}`}>{p.name || p.purpose}: not checked in</option>
            </React.Fragment>
          ))}
        </select>
      </label>
      <label className="space-y-1 text-xs text-gray-600">
        <span>Form field</span>
        <select className={selectClass} value={value.field} onChange={(e) => set({ field: e.target.value, value: '' })}>
          <option value="">None</option>
          {fields.map((f) => (
            <option key={f.label} value={f.label}>{f.label}</option>
          ))}
        </select>
      </label>
      <label className="space-y-1 text-xs text-gray-600">
        <span>Answer</span>
        {selectedField?.options.length ? (
          <select className={selectClass} value={value.value} onChange={(e) => set({ value: e.target.value })}>
            <option value="">Select…</option>
            {selectedField.options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ) : (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              set({ value: draftValue.trim() });
            }}
          >
            <Input
              className="h-9"
              placeholder={value.field ? 'Exact answer, then Enter' : 'Pick a field first'}
              disabled={!value.field}
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
            />
            <Button type="submit" size="sm" variant="outline" disabled={!value.field}>Apply</Button>
          </form>
        )}
      </label>
    </div>
  );
};

export default RegistrationFilterBar;
